import React, { useState, useEffect, useContext } from "react";
import "./Payments-Table.scss";

//import components
import PaymentConfirmationModal from "./ApprovePaymentModal";

//icons import
import { FaPercentage } from "react-icons/fa";
import { MdCreditScore } from "react-icons/md";
import { FaUndo } from "react-icons/fa";
import { IoCheckmarkDoneSharp } from "react-icons/io5";
import { FaChevronDown, FaChevronRight } from "react-icons/fa";
//import context
import { MyContext } from "../../../../tenantsDashContext";

//redux imports
import { useSelector, useDispatch } from "react-redux";
import {
  fetchPayments,
  confirmPayments,
  reversePayments,
} from "../../../../redux/slices/sliceActions";

// --- Added helper to safely produce ISO strings from possibly invalid dates ---
const safeISO = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d.toISOString();
};

const getISO = (primary, fallback) => {
  return safeISO(primary) || safeISO(fallback) || new Date().toISOString();
};

const formatPayments = (payments = []) => {
  return payments.map((payment) => ({
    payment_id_to_display: `PAY${payment._id?.slice(-3)}`,
    house_no: payment?.tenant?.house?.house_no,
    payment_id: payment._id,
    payment_status: payment.status === "completed" ? 1 : 0,
    approval_status: payment.status === "completed" ? 1 : 0,
    created_at: safeISO(payment.createdAt) || "",
    payment_date: getISO(payment.payment_date, payment.createdAt),
    invoice: `INV${payment._id?.slice(-3)}`,
    rent_amount: payment.rent || 0,
    water_bill_amount: payment?.water || 0,
    garbage_charge_amount: payment?.garbage || 0,
    service_fee_amount: payment.service_charge || 0,
    unpaid_deposit: payment?.deposit || 0,
    amount: payment.amount,
    arrears_amount: payment?.arrears || 0,
    late_fee: payment?.late_fee,
    narration: payment?.narration || "",
    reference: payment?.reference || "",
    tenant: payment.tenant,
    status: payment.status,
    method: payment.method || "N/A",
  }));
};
// --- end helper ---

const TransactionsTable = ({
  approved,
  awaitingApproval,
  selectedApartmentId,
  selectedMonth,
  selectedYear,
  monthChoices,
}) => {
  // redux methods
  const dispatch = useDispatch();
  const { payments, properties } = useSelector((state) => state.landlordslice);

  //check shared value from context
  const { sharedValue, setSharedValue } = useContext(MyContext);

  const [paymentsReceived, setPaymentsReceived] = useState([]);
  const [groupedPayments, setGroupedPayments] = useState([]);
  const [visiblePayments, setVisiblePayments] = useState(3); // State to manage visible payments
  const [expandedPayment, setExpandedPayment] = useState(null); // State to track which payment is expanded
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  // Function to group payments by tenant
  const groupPaymentsByTenant = (payments) => {
    const grouped = payments.reduce((acc, payment) => {
      const tenantId = payment.tenant?._id;
      if (!acc[tenantId]) {
        acc[tenantId] = {
          tenantId,
          house_no: payment.tenant?.house?.house_no,
          payments: [],
          totalAmount: 0,
          totalRent: 0,
          totalWater: 0,
          totalGarbage: 0,
          totalServiceFee: 0,
          totalArrears: 0,
          totalDeposit: 0,
          totalLateFee: 0,
        };
      }
      acc[tenantId].payments.push(payment);
      acc[tenantId].totalAmount += payment.amount || 0;
      acc[tenantId].totalRent += payment.rent_amount || 0;
      acc[tenantId].totalWater += payment.water_bill_amount || 0;
      acc[tenantId].totalGarbage += payment.garbage_charge_amount || 0;
      acc[tenantId].totalServiceFee += payment.service_fee_amount || 0;
      acc[tenantId].totalArrears += payment.arrears_amount || 0;
      acc[tenantId].totalDeposit += payment.unpaid_deposit || 0;
      acc[tenantId].totalLateFee += payment.late_fee || 0;

      return acc;
    }, {});

    return Object.values(grouped);
  };

  useEffect(() => {
    console.log("payments", payments);

    if (!payments?.length) {
      setPaymentsReceived([]);
      setGroupedPayments([]);
      return;
    }

    const filteredPayments = awaitingApproval
      ? payments.filter(
          (payment) =>
            payment.status === "pending" || payment.status === "reversed",
        )
      : approved === false
        ? payments.filter((payment) => payment.status !== "completed")
        : payments.filter((payment) => payment.status === "completed");

    const formattedPayments = formatPayments(filteredPayments);
    setPaymentsReceived(formattedPayments);
    setGroupedPayments(groupPaymentsByTenant(formattedPayments));
  }, [payments, awaitingApproval, approved]);

  const paymentData = {
    payments: groupedPayments,
  };

  // Function to toggle group expansion
  const toggleGroupExpansion = (tenantId) => {
    const newExpandedGroups = new Set(expandedGroups);
    if (newExpandedGroups.has(tenantId)) {
      newExpandedGroups.delete(tenantId);
    } else {
      newExpandedGroups.add(tenantId);
    }
    setExpandedGroups(newExpandedGroups);
  };

  // Function to show more payments
  const showMorePayments = () => {
    setVisiblePayments((prev) =>
      Math.min(prev + 3, paymentData.payments.length),
    );
  };

  // Function to hide all payments except the first three
  const hideAllPayments = () => {
    setVisiblePayments(3);
    setExpandedPayment(null); // Collapse any expanded payment
    setExpandedGroups(new Set());
  };

  // Function to handle individual payment click
  const handlePaymentClick = (paymentId) => {
    setExpandedPayment((prev) => (prev === paymentId ? null : paymentId));
  };

  //approve payment
  const [isOpen, setIsOpen] = useState(false);
  const closeModal = () => {
    setIsOpen(false);
  };

  const handleConfrim = () => {
    if (properties.length < 1) {
      return;
    }

    const paymentId = expandedPayment;

    // Find the specific payment to get its createdAt date
    const currentPayment = payments.find(
      (payment) => payment._id === paymentId,
    );
    if (!currentPayment) {
      console.error("Payment not found");
      return;
    }

    dispatch(confirmPayments({ paymentId })).then((res) => {
      // Extract month and year from payment's createdAt
      const paymentDate = new Date(currentPayment.createdAt);
      const monthNumber = paymentDate.getMonth() + 1; // 0-based index, so add 1
      const yearNumber = paymentDate.getFullYear();

      // Use the first property ID if selectedApartmentId is not provided
      const propertyId =
        selectedApartmentId ||
        (properties.length > 0 ? properties[0]._id : null);
      console.log("the property id", propertyId);
      if (!propertyId) {
        console.error("No property ID available");
        return;
      }
      setIsOpen(false);
      dispatch(
        fetchPayments({
          propertyId,
          month: monthNumber,
          year: yearNumber,
        }),
      );
    });

    console.log("cleared");
  };

  //reverse payment
  const [isReverse, setIsReverse] = useState(false);
  const handleReverse = (paymentId) => {
    // Find the specific payment to get its createdAt date
    const currentPayment = payments.find(
      (payment) => payment._id === paymentId,
    );
    if (!currentPayment) {
      console.error("Payment not found");
      return;
    }

    dispatch(reversePayments({ paymentId })).then((res) => {
      // Extract month and year from payment's createdAt
      const paymentDate = new Date(currentPayment.createdAt);
      const monthNumber = paymentDate.getMonth() + 1; // 0-based index, so add 1
      const yearNumber = paymentDate.getFullYear();

      // Use the first property ID if selectedApartmentId is not provided
      const propertyId =
        selectedApartmentId ||
        (properties.length > 0 ? properties[0]._id : null);
      console.log("the property id", propertyId);
      if (!propertyId) {
        console.error("No property ID available");
        return;
      }
      setIsOpen(false);
      dispatch(
        fetchPayments({
          propertyId,
          month: monthNumber,
          year: yearNumber,
        }),
      );
    });

    console.log("cleared");
  };

  return (
    <>
      {groupedPayments?.length > 0 ? (
        <>
          <div className="table-container-pc">
            <table>
              <thead>
                <tr>
                  <th style={{ textAlign: "center" }}>House Number</th>
                  <th>Rent</th>
                  <th>Water</th>
                  <th>Garbage</th>
                  <th>Service Fee</th>
                  <th>Arrears</th>
                  <th>Unpaid deposit</th>
                  <th>Late Fee</th>
                  <th>Total</th>
                  <th>Approval Status</th>
                  <th>Payment Reference</th>
                  <th>Payment Method</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {paymentData.payments
                  .slice(0, visiblePayments)
                  .map((group, groupIndex) => (
                    <React.Fragment key={group.tenantId || groupIndex}>
                      {/* Group Header Row */}
                      <tr
                        onClick={() => toggleGroupExpansion(group.tenantId)}
                        className="clickable-row group-header-row"
                      >
                        <td>
                          {/* {expandedGroups.has(group.tenantId) ? (
                            <FaChevronDown />
                          ) : (
                            <FaChevronRight />
                          )} */}
                          {group.house_no} ({group.payments.length} payments)
                        </td>
                        <td>{group.totalRent}</td>
                        <td>{group.totalWater}</td>
                        <td>{group.totalGarbage}</td>
                        <td>{group.totalServiceFee}</td>
                        <td>{group.totalArrears}</td>
                        <td>{group.totalDeposit}</td>
                        <td>{group.totalLateFee}</td>
                        <td>{group.totalAmount}</td>
                        <td>Multiple</td>
                        <td>Multiple</td>
                        <td>Multiple</td>
                        <td>-</td>
                      </tr>

                      {/* Individual Payment Rows */}
                      {expandedGroups.has(group.tenantId) &&
                        group.payments.map((payment, paymentIndex) => (
                          <tr
                            key={payment.payment_id || paymentIndex}
                            className="individual-payment-row"
                            onClick={() =>
                              handlePaymentClick(payment.payment_id)
                            }
                          >
                            <td style={{ paddingLeft: "30px" }}>
                              {/* Display payment date in dd/mm/yy format */}
                              {(() => {
                                const date = new Date(payment.payment_date);
                                const day = String(date.getDate()).padStart(
                                  2,
                                  "0",
                                );
                                const month = String(
                                  date.getMonth() + 1,
                                ).padStart(2, "0");
                                const year = String(date.getFullYear()).slice(
                                  -2,
                                );
                                return `${day}/${month}/${year}`;
                              })()}
                            </td>
                            <td>{payment.rent_amount}</td>
                            <td>{payment.water_bill_amount}</td>
                            <td>{payment.garbage_charge_amount}</td>
                            <td>{payment.service_fee_amount}</td>
                            <td>{payment.arrears_amount}</td>
                            <td>{payment.unpaid_deposit}</td>
                            <td>{payment.late_fee}</td>
                            <td>{payment.amount}</td>
                            <td
                              className={`dashboard-status ${
                                payment.approval_status === 0
                                  ? "pending"
                                  : "completed"
                              }`}
                            >
                              <div className="status-wrapper">
                                {payment.approval_status === 0
                                  ? "Pending"
                                  : "Completed"}
                              </div>
                            </td>
                            <td>{payment.reference || "No Reference"}</td>
                            <td>{payment.method || "N/A"}</td>
                            <td>
                              <button
                                className="reverse-payment"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (payment.approval_status === 0) {
                                    setIsReverse(false);
                                    setIsOpen(true);
                                    setExpandedPayment(payment.payment_id);
                                    return;
                                  } else {
                                    setIsReverse(true);
                                    setIsOpen(true);
                                    setExpandedPayment(payment.payment_id);
                                  }
                                }}
                              >
                                {payment.approval_status === 0 ? (
                                  <IoCheckmarkDoneSharp
                                    className="undo-icon"
                                    size={25}
                                  />
                                ) : (
                                  <FaUndo className="undo-icon" size={19} />
                                )}
                              </button>
                            </td>
                          </tr>
                        ))}
                    </React.Fragment>
                  ))}
              </tbody>
            </table>

            {/* Buttons to show more or hide payments */}
            <div className="button-container">
              {visiblePayments < paymentData.payments.length && (
                <button onClick={showMorePayments}>Show More</button>
              )}
              {visiblePayments > 3 && (
                <button onClick={hideAllPayments}>Hide All</button>
              )}
            </div>

            <PaymentConfirmationModal
              onReverse={handleReverse}
              isReverse={isReverse}
              isOpen={isOpen}
              paymentId={expandedPayment}
              onConfirm={handleConfrim}
              onCancel={closeModal}
            />
          </div>
        </>
      ) : (
        <>
          <div>No Payments Yet</div>
        </>
      )}
    </>
  );
};

export default TransactionsTable;
